/**
 * Loadout Stats Panel - Real-time stats summary for loadout builder
 * Shows aggregated stats that update when components are swapped
 */

import React, { useMemo } from 'react';
import type { Ship, ShipHardpoint } from '../../services/shipService';
import type { ComponentItem } from '../../services/componentService';
import { Shield, Zap, Crosshair, Gauge, Thermometer, Radio, Magnet, Pickaxe, Recycle, Rocket } from 'lucide-react';
import styles from './LoadoutStatsPanel.module.css';

interface LoadoutStatsPanelProps {
  ship: Ship;
  hardpoints: ShipHardpoint[];
  hardpointOverrides: Map<number, ComponentItem>;
  loadedItemOverrides?: Map<string, ComponentItem>;
}

interface StatChange {
  current: number;
  stock: number;
  delta: number;
  improved: boolean;
}

export const LoadoutStatsPanel: React.FC<LoadoutStatsPanelProps> = ({
  ship,
  hardpoints,
  hardpointOverrides,
  loadedItemOverrides
}) => {
  // Calculate shield stats
  const shieldStats = useMemo(() => {
    const shieldHardpoints = hardpoints.filter(h => {
      const itemType = h.itemDefinition?.type?.toLowerCase() || '';
      return (h.hardpointType === 'Shield' || itemType === 'shield' || itemType === 'shieldgenerator') &&
             itemType !== 'shieldcontroller';
    });

    let totalHP = 0;
    let stockHP = 0;
    let totalRegen = 0;
    let stockRegen = 0;

    const shields = shieldHardpoints.map(hp => {
      const override = hardpointOverrides.get(hp.id);
      const stockShield = hp.itemDefinition;
      
      // Current values
      const currentItem = override || stockShield;
      const currentHP = currentItem?.shieldStats?.shieldHealthMax || 0;
      const currentRegen = currentItem?.shieldStats?.regenRate || 0;
      const currentDownedDelay = currentItem?.shieldStats?.downedRegenDelay || 0;
      const currentDamagedDelay = currentItem?.shieldStats?.damagedRegenDelay || 0;
      const stockHPValue = stockShield?.shieldStats?.shieldHealthMax || 0;
      const stockRegenValue = stockShield?.shieldStats?.regenRate || 0;
      const stockDownedDelay = stockShield?.shieldStats?.downedRegenDelay || 0;
      const stockDamagedDelay = stockShield?.shieldStats?.damagedRegenDelay || 0;
      
      totalHP += currentHP;
      totalRegen += currentRegen;
      stockHP += stockHPValue;
      stockRegen += stockRegenValue;
      
      return {
        name: currentItem?.localizedName || currentItem?.itemName || 'Unknown Shield',
        stockName: stockShield?.localizedName || stockShield?.itemName || 'Unknown Shield',
        manufacturer: currentItem?.manufacturer || 'Unknown',
        size: currentItem?.size || stockShield?.size || 1,
        grade: currentItem?.grade || stockShield?.grade || 3,
        hp: { current: currentHP, stock: stockHPValue, delta: currentHP - stockHPValue, improved: currentHP > stockHPValue },
        regen: { current: currentRegen, stock: stockRegenValue, delta: currentRegen - stockRegenValue, improved: currentRegen > stockRegenValue },
        downedDelay: { current: currentDownedDelay, stock: stockDownedDelay, delta: currentDownedDelay - stockDownedDelay, improved: currentDownedDelay < stockDownedDelay },
        damagedDelay: { current: currentDamagedDelay, stock: stockDamagedDelay, delta: currentDamagedDelay - stockDamagedDelay, improved: currentDamagedDelay < stockDamagedDelay },
        isModified: !!override
      };
    });

    return {
      hp: { current: totalHP, stock: stockHP, delta: totalHP - stockHP, improved: totalHP > stockHP },
      regen: { current: totalRegen, stock: stockRegen, delta: totalRegen - stockRegen, improved: totalRegen > stockRegen },
      count: shieldHardpoints.length,
      shields
    };
  }, [hardpoints, hardpointOverrides]);

  // Calculate power plant stats
  const powerStats = useMemo(() => {
    const powerPlantHardpoints = hardpoints.filter(h => 
      h.hardpointType === 'PowerPlant' ||
      h.itemDefinition?.type?.toLowerCase().includes('powerplant')
    );

    let totalPips = 0;
    let stockPips = 0;
    let totalOutput = 0;
    let stockOutput = 0;

    const plants = powerPlantHardpoints.map(hp => {
      const override = hardpointOverrides.get(hp.id);
      const stockPlant = hp.itemDefinition;
      
      // Current values
      const currentItem = override || stockPlant;
      const currentPips = currentItem?.powerPipStats?.powerUnits || 0;
      const currentOutput = currentItem?.powerPlantStats?.powerOutput || 0;
      const stockPipsValue = stockPlant?.powerPipStats?.powerUnits || 0;
      const stockOutputValue = stockPlant?.powerPlantStats?.powerOutput || 0;
      
      totalPips += currentPips;
      totalOutput += currentOutput;
      stockPips += stockPipsValue;
      stockOutput += stockOutputValue;
      
      return {
        name: currentItem?.localizedName || currentItem?.itemName || 'Unknown Power Plant',
        stockName: stockPlant?.localizedName || stockPlant?.itemName || 'Unknown Power Plant',
        manufacturer: currentItem?.manufacturer || 'Unknown',
        size: currentItem?.size || stockPlant?.size || 1,
        grade: currentItem?.grade || stockPlant?.grade || 3,
        pips: { current: currentPips, stock: stockPipsValue, delta: currentPips - stockPipsValue, improved: currentPips > stockPipsValue },
        output: { current: currentOutput, stock: stockOutputValue, delta: currentOutput - stockOutputValue, improved: currentOutput > stockOutputValue },
        isModified: !!override
      };
    });

    return {
      pips: { current: totalPips, stock: stockPips, delta: totalPips - stockPips, improved: totalPips > stockPips },
      output: { current: totalOutput, stock: stockOutput, delta: totalOutput - stockOutput, improved: totalOutput > stockOutput },
      count: powerPlantHardpoints.length,
      plants
    };
  }, [hardpoints, hardpointOverrides]);

  // Calculate cooler stats
  const coolerStats = useMemo(() => {
    const coolerHardpoints = hardpoints.filter(h => {
      // Must be a cooler hardpoint
      if (h.hardpointType !== 'Cooler') return false;
      // Exclude controller items - these are system controllers, not actual coolers
      if (h.defaultItemName?.toLowerCase().startsWith('controller_')) return false;
      if (h.slotName?.toLowerCase().includes('controller')) return false;
      return true;
    });

    let totalCooling = 0;
    let stockCooling = 0;
    const coolers: Array<{
      name: string;
      manufacturer: string;
      size: number;
      grade: number;
      coolingRate: number;
      pips: number;
      health: number;
      isModified: boolean;
    }> = [];

    coolerHardpoints.forEach(hp => {
      const override = hardpointOverrides.get(hp.id);
      const stockCooler = hp.itemDefinition;
      const currentCooler = override || stockCooler;
      
      if (currentCooler) {
        const currentRate = currentCooler.coolerStats?.coolingRate || 0;
        const stockRate = stockCooler?.coolerStats?.coolingRate || 0;
        
        totalCooling += currentRate;
        stockCooling += stockRate;
        
        coolers.push({
          name: currentCooler.localizedName || currentCooler.itemName,
          manufacturer: currentCooler.manufacturer || 'Unknown',
          size: currentCooler.size || 1,
          grade: currentCooler.grade || 3,
          coolingRate: currentRate,
          pips: currentCooler.powerPipStats?.powerUnits || 3,
          health: currentCooler.health || 0,
          isModified: !!override
        });
      }
    });

    return {
      cooling: { current: totalCooling, stock: stockCooling, delta: totalCooling - stockCooling, improved: totalCooling > stockCooling },
      count: coolerHardpoints.length,
      coolers
    };
  }, [hardpoints, hardpointOverrides]);

  // Calculate quantum drive stats
  const quantumStats = useMemo(() => {
    const qdHardpoint = hardpoints.find(h => 
      h.hardpointType === 'QuantumDrive' ||
      h.itemDefinition?.type?.toLowerCase() === 'quantumdrive'
    );

    if (!qdHardpoint) return null;

    const override = hardpointOverrides.get(qdHardpoint.id);
    const stockQD = qdHardpoint.itemDefinition;
    const currentQD = override || stockQD;

    if (!currentQD) return null;

    const currentStats = currentQD.quantumDriveStats;
    const stockStats = stockQD?.quantumDriveStats;

    return {
      name: currentQD.localizedName || currentQD.itemName,
      stockName: stockQD?.localizedName || stockQD?.itemName || '',
      manufacturer: currentQD.manufacturer || 'Unknown',
      size: currentQD.size || 1,
      grade: currentQD.grade || 3,
      isModified: !!override,
      pips: currentQD.powerPipStats?.powerUnits || 3,
      health: currentQD.health || 0,
      speed: {
        current: currentStats?.driveSpeed || 0,
        stock: stockStats?.driveSpeed || 0,
        delta: (currentStats?.driveSpeed || 0) - (stockStats?.driveSpeed || 0),
        improved: (currentStats?.driveSpeed || 0) > (stockStats?.driveSpeed || 0)
      },
      spool: {
        current: currentStats?.spoolUpTime || 0,
        stock: stockStats?.spoolUpTime || 0,
        delta: (currentStats?.spoolUpTime || 0) - (stockStats?.spoolUpTime || 0),
        improved: (currentStats?.spoolUpTime || 0) < (stockStats?.spoolUpTime || 0) // Lower is better
      },
      cooldown: {
        current: currentStats?.cooldownTime || 0,
        stock: stockStats?.cooldownTime || 0,
        delta: (currentStats?.cooldownTime || 0) - (stockStats?.cooldownTime || 0),
        improved: (currentStats?.cooldownTime || 0) < (stockStats?.cooldownTime || 0) // Lower is better
      },
      fuel: {
        current: currentStats?.fuelRequirement || 0,
        stock: stockStats?.fuelRequirement || 0,
        delta: (currentStats?.fuelRequirement || 0) - (stockStats?.fuelRequirement || 0),
        improved: (currentStats?.fuelRequirement || 0) < (stockStats?.fuelRequirement || 0) // Lower is better
      }
    };
  }, [hardpoints, hardpointOverrides]);

  // Calculate weapon stats
  const weaponStats = useMemo(() => {
    // Helper to calculate DPS
    const calcDPS = (fireRate: number | null, dmg: number | null): number => {
      if (!fireRate || !dmg) return 0;
      return (dmg * fireRate) / 60;
    };

    // Helper to get damage type
    const getDamageType = (itemDef: any, itemName: string): 'ballistic' | 'energy' | 'distortion' | 'unknown' => {
      const damageType = (itemDef?.weaponStats?.damageType || '').toLowerCase();
      if (damageType.includes('ballistic') || damageType.includes('physical')) return 'ballistic';
      if (damageType.includes('energy') || damageType.includes('laser')) return 'energy';
      if (damageType.includes('distortion') || damageType.includes('emp')) return 'distortion';
      
      const name = (itemName || itemDef?.localizedName || '').toLowerCase();
      if (name.includes('repeater') || name.includes('cannon') || name.includes('gatling') || 
          name.includes('scattergun') || name.includes('ballistic')) return 'ballistic';
      if (name.includes('laser') || name.includes('plasma') || name.includes('beam') || 
          name.includes('neutron') || name.includes('pulsar')) return 'energy';
      if (name.includes('distortion') || name.includes('suckerpunch') || name.includes('emp')) return 'distortion';
      return 'unknown';
    };

    // Helper to recursively get weapons from loadedItems
    // Pass hardpointId = -1 to skip override checking (for stock calculations)
    const getWeaponsFromLoadedItems = (loadedItems: any[], hardpointId: number): { dps: number; damageType: string }[] => {
      const weapons: { dps: number; damageType: string }[] = [];
      loadedItems.forEach(item => {
        const itemType = (item.itemDefinition?.type || '').toLowerCase();
        const itemName = (item.itemName || '').toLowerCase();
        
        // Skip controller items
        if (itemName.includes('controller') || itemName.includes('turretbase')) return;
        
        // Check if this loaded item has been overridden (only if hardpointId >= 0)
        let effectiveItemDef = item.itemDefinition;
        let effectiveItemName = item.itemName;
        
        if (hardpointId >= 0) {
          const overrideKey = `${hardpointId}:${item.id}`;
          const overrideItem = loadedItemOverrides?.get(overrideKey);
          if (overrideItem) {
            effectiveItemDef = overrideItem;
            effectiveItemName = overrideItem.itemName;
          }
        }
        
        // Check if actual weapon
        if ((itemType === 'weapon' || itemType === 'weapongun') && effectiveItemDef?.weaponStats) {
          const ws = effectiveItemDef.weaponStats;
          weapons.push({
            dps: calcDPS(ws.fireRate, ws.damagePerShot),
            damageType: getDamageType(effectiveItemDef, effectiveItemName)
          });
        }
        
        // Check children
        if (item.childAttachments?.length > 0) {
          weapons.push(...getWeaponsFromLoadedItems(item.childAttachments, hardpointId));
        }
      });
      return weapons;
    };

    // Track both stock and current values for delta comparison
    let currentTotalDPS = 0;
    let currentBallisticDPS = 0;
    let currentEnergyDPS = 0;
    let currentDistortionDPS = 0;
    let stockTotalDPS = 0;
    let stockBallisticDPS = 0;
    let stockEnergyDPS = 0;
    let stockDistortionDPS = 0;
    let weaponCount = 0;

    // Process weapon hardpoints (includes direct weapons AND turrets)
    const weaponHardpoints = hardpoints.filter(hp => {
      // Include both direct weapon hardpoints and turrets
      if (hp.hardpointType === 'Weapon' || hp.hardpointType === 'Turret' || 
          hp.hardpointType === 'TurretBase' || hp.hardpointType === 'MannedTurret') {
        const slotName = (hp.slotName || '').toLowerCase();
        const defaultName = (hp.defaultItemName || '').toLowerCase();
        // Exclude controllers
        if (slotName.includes('controller') || defaultName.includes('controller')) return false;
        return true;
      }
      return false;
    });

    weaponHardpoints.forEach(hp => {
      // Check if this hardpoint has an override
      const override = hardpointOverrides.get(hp.id);
      
      // Calculate STOCK values first (always from original loadout)
      if (hp.loadedItems && hp.loadedItems.length > 0) {
        // Get stock weapons WITHOUT checking overrides
        const stockWeapons = getWeaponsFromLoadedItems(hp.loadedItems as any[], -1); // Use -1 to skip override lookup
        stockWeapons.forEach(w => {
          if (w.dps > 0) {
            stockTotalDPS += w.dps;
            if (w.damageType === 'ballistic') stockBallisticDPS += w.dps;
            else if (w.damageType === 'energy') stockEnergyDPS += w.dps;
            else if (w.damageType === 'distortion') stockDistortionDPS += w.dps;
          }
        });
      } else if (hp.itemDefinition?.weaponStats) {
        const ws = hp.itemDefinition.weaponStats;
        const dps = calcDPS(ws.fireRate ?? null, ws.damagePerShot ?? null);
        const damageType = getDamageType(hp.itemDefinition, hp.defaultItemName || '');
        if (dps > 0) {
          stockTotalDPS += dps;
          if (damageType === 'ballistic') stockBallisticDPS += dps;
          else if (damageType === 'energy') stockEnergyDPS += dps;
          else if (damageType === 'distortion') stockDistortionDPS += dps;
        }
      }
      
      // Calculate CURRENT values (with overrides)
      if (override?.weaponStats) {
        // Use override weapon stats (for direct weapon swaps)
        const ws = override.weaponStats;
        const dps = calcDPS(ws.fireRate ?? null, ws.damagePerShot ?? null);
        const damageType = getDamageType(override, override.itemName || '');
        
        if (dps > 0) {
          currentTotalDPS += dps;
          if (damageType === 'ballistic') currentBallisticDPS += dps;
          else if (damageType === 'energy') currentEnergyDPS += dps;
          else if (damageType === 'distortion') currentDistortionDPS += dps;
        }
        weaponCount++;
      } else if (hp.loadedItems && hp.loadedItems.length > 0) {
        // Check loadedItems for mounted weapons WITH overrides
        const weapons = getWeaponsFromLoadedItems(hp.loadedItems as any[], hp.id);
        weapons.forEach(w => {
          if (w.dps > 0) {
            currentTotalDPS += w.dps;
            weaponCount++;
            if (w.damageType === 'ballistic') currentBallisticDPS += w.dps;
            else if (w.damageType === 'energy') currentEnergyDPS += w.dps;
            else if (w.damageType === 'distortion') currentDistortionDPS += w.dps;
          } else {
            weaponCount++;
          }
        });
      } else if (hp.itemDefinition?.weaponStats) {
        // Direct weapon on hardpoint (stock, no override)
        const ws = hp.itemDefinition.weaponStats;
        const dps = calcDPS(ws.fireRate ?? null, ws.damagePerShot ?? null);
        const damageType = getDamageType(hp.itemDefinition, hp.defaultItemName || '');
        
        if (dps > 0) {
          currentTotalDPS += dps;
          if (damageType === 'ballistic') currentBallisticDPS += dps;
          else if (damageType === 'energy') currentEnergyDPS += dps;
          else if (damageType === 'distortion') currentDistortionDPS += dps;
        }
        weaponCount++;
      }
    });

    // Collect individual weapon details for display
    const weapons: Array<{
      name: string;
      manufacturer: string;
      size: number;
      grade: number;
      damageType: 'ballistic' | 'energy' | 'distortion' | 'unknown';
      dps: number;
      fireRate: number;
      damagePerShot: number;
      range: number;
      velocity: number;
      ammoCapacity: number;
      isModified: boolean;
      quantity: number;
    }> = [];
    
    const weaponGroups = new Map<string, {
      itemDef: any;
      override: any;
      count: number;
    }>();
    
    weaponHardpoints.forEach(hp => {
      const override = hardpointOverrides.get(hp.id);
      
      // Direct weapon swap (override at hardpoint level)
      if (override?.weaponStats) {
        const key = override.itemName || 'unknown';
        const existing = weaponGroups.get(key);
        if (existing) {
          existing.count++;
        } else {
          weaponGroups.set(key, {
            itemDef: null,
            override: override,
            count: 1
          });
        }
      } else if (hp.loadedItems && hp.loadedItems.length > 0) {
        // Collect weapons from loadedItems (turrets, mounts, etc.)
        hp.loadedItems.forEach((item: any) => {
          const itemType = (item.itemDefinition?.type || '').toLowerCase();
          const itemName = (item.itemName || '').toLowerCase();
          
          if (itemName.includes('controller') || itemName.includes('turretbase')) return;
          
          // Check for override on this loaded item
          const overrideKey = `${hp.id}:${item.id}`;
          const loadedOverride = loadedItemOverrides?.get(overrideKey);
          
          if ((itemType === 'weapon' || itemType === 'weapongun') && (loadedOverride || item.itemDefinition?.weaponStats)) {
            const effectiveItem = loadedOverride || item.itemDefinition;
            const key = effectiveItem.itemName || 'unknown';
            const existing = weaponGroups.get(key);
            if (existing) {
              existing.count++;
            } else {
              weaponGroups.set(key, {
                itemDef: loadedOverride ? null : item.itemDefinition,
                override: loadedOverride || null,
                count: 1
              });
            }
          }
          
          // Check child attachments (weapons inside turrets)
          if (item.childAttachments && item.childAttachments.length > 0) {
            item.childAttachments.forEach((child: any) => {
              const childType = (child.itemDefinition?.type || '').toLowerCase();
              const childName = (child.itemName || '').toLowerCase();
              
              if (childName.includes('controller')) return;
              
              const childOverrideKey = `${hp.id}:${child.id}`;
              const childOverride = loadedItemOverrides?.get(childOverrideKey);
              
              if ((childType === 'weapon' || childType === 'weapongun') && (childOverride || child.itemDefinition?.weaponStats)) {
                const effectiveChild = childOverride || child.itemDefinition;
                const key = effectiveChild.itemName || 'unknown';
                const existing = weaponGroups.get(key);
                if (existing) {
                  existing.count++;
                } else {
                  weaponGroups.set(key, {
                    itemDef: childOverride ? null : child.itemDefinition,
                    override: childOverride || null,
                    count: 1
                  });
                }
              }
            });
          }
        });
      } else if (hp.itemDefinition?.weaponStats) {
        // Direct weapon on hardpoint (stock)
        const key = hp.itemDefinition.itemName || 'unknown';
        const existing = weaponGroups.get(key);
        if (existing) {
          existing.count++;
        } else {
          weaponGroups.set(key, {
            itemDef: hp.itemDefinition,
            override: null,
            count: 1
          });
        }
      }
    });
    
    // Convert weaponGroups to display array
    weaponGroups.forEach((group, key) => {
      const weaponDef = group.override || group.itemDef;
      if (!weaponDef || !weaponDef.weaponStats) return;
      
      const ws = weaponDef.weaponStats;
      weapons.push({
        name: weaponDef.localizedName || weaponDef.itemName || 'Unknown Weapon',
        manufacturer: weaponDef.manufacturer || 'Unknown',
        size: weaponDef.size || 1,
        grade: weaponDef.grade || 3,
        damageType: getDamageType(weaponDef, weaponDef.itemName || ''),
        dps: calcDPS(ws.fireRate ?? null, ws.damagePerShot ?? null),
        fireRate: ws.fireRate || 0,
        damagePerShot: ws.damagePerShot || 0,
        range: ws.range || 0,
        velocity: ws.velocity || 0,
        ammoCapacity: ws.ammoCapacity || 0,
        isModified: !!group.override,
        quantity: group.count
      });
    });

    return {
      count: weaponCount,
      totalDPS: {
        current: currentTotalDPS,
        stock: stockTotalDPS,
        delta: currentTotalDPS - stockTotalDPS,
        improved: currentTotalDPS > stockTotalDPS
      },
      ballisticDPS: {
        current: currentBallisticDPS,
        stock: stockBallisticDPS,
        delta: currentBallisticDPS - stockBallisticDPS,
        improved: currentBallisticDPS > stockBallisticDPS
      },
      energyDPS: {
        current: currentEnergyDPS,
        stock: stockEnergyDPS,
        delta: currentEnergyDPS - stockEnergyDPS,
        improved: currentEnergyDPS > stockEnergyDPS
      },
      distortionDPS: {
        current: currentDistortionDPS,
        stock: stockDistortionDPS,
        delta: currentDistortionDPS - stockDistortionDPS,
        improved: currentDistortionDPS > stockDistortionDPS
      },
      weapons
    };
  }, [hardpoints, hardpointOverrides, loadedItemOverrides]);

  // Calculate missile stats
  const missileStats = useMemo(() => {
    const missileHardpoints = hardpoints.filter(hp => 
      hp.hardpointType === 'MissileRack' || hp.componentType === 'MissileLauncher'
    );

    if (missileHardpoints.length === 0) return null;

    let currentTotalDamage = 0;
    let stockTotalDamage = 0;
    let missileCount = 0;
    const missiles: Array<{
      name: string;
      manufacturer: string;
      size: number;
      grade: number;
      type: string;
      damage: number;
      speed: number;
      lockTime: number;
      launchDelay: number;
      lockingRange: number;
      isModified: boolean;
      quantity: number;
    }> = [];
    const missileGroups = new Map<string, {
      itemDef: any;
      override: any;
      count: number;
    }>();

    missileHardpoints.forEach(hp => {
      // Check if this rack has a missile override (applies to ALL missiles in rack)
      let rackOverride: ComponentItem | undefined = undefined;
      
      // Find first missile and check for override
      if (hp.loadedItems && hp.loadedItems.length > 0) {
        for (const loadedItem of hp.loadedItems) {
          const itemDef = loadedItem.itemDefinition;
          const itemType = (itemDef?.type || '').toLowerCase();
          
          if (itemDef && (itemType.includes('missile') || itemType.includes('torpedo') || itemType.includes('bomb'))) {
            const overrideKey = `${hp.id}:${loadedItem.id}`;
            rackOverride = loadedItemOverrides?.get(overrideKey);
            break; // Found first missile, rack override applies to all
          }
          
          // Check children
          if (loadedItem.childAttachments && loadedItem.childAttachments.length > 0) {
            for (const child of loadedItem.childAttachments) {
              const childItemDef = child.itemDefinition;
              const childItemType = (childItemDef?.type || '').toLowerCase();
              
              if (childItemDef && (childItemType.includes('missile') || childItemType.includes('torpedo') || childItemType.includes('bomb'))) {
                const overrideKey = `${hp.id}:${child.id}`;
                rackOverride = loadedItemOverrides?.get(overrideKey);
                break;
              }
            }
            if (rackOverride !== undefined) break;
          }
        }
      }
      
      // Now count all missiles and apply rack override to all
      if (hp.loadedItems && hp.loadedItems.length > 0) {
        hp.loadedItems.forEach(loadedItem => {
          const itemDef = loadedItem.itemDefinition;
          const itemType = (itemDef?.type || '').toLowerCase();
          
          // Check for missile/torpedo/bomb
          if (itemDef && (itemType.includes('missile') || itemType.includes('torpedo') || itemType.includes('bomb'))) {
            // Stock damage
            const stockDamage = itemDef.missileStats?.damage || 0;
            stockTotalDamage += stockDamage;
            
            // Current damage - use rack override if present
            const currentDamage = rackOverride?.missileStats?.damage || stockDamage;
            currentTotalDamage += currentDamage;
            missileCount++;
            
            // Group by missile type
            const key = rackOverride ? rackOverride.itemName : itemDef.itemName;
            const existing = missileGroups.get(key);
            if (existing) {
              existing.count++;
            } else {
              missileGroups.set(key, {
                itemDef,
                override: rackOverride,
                count: 1
              });
            }
          }
          
          // Check child attachments
          if (loadedItem.childAttachments && loadedItem.childAttachments.length > 0) {
            loadedItem.childAttachments.forEach((child: any) => {
              const childItemDef = child.itemDefinition;
              const childItemType = (childItemDef?.type || '').toLowerCase();
              
              if (childItemDef && (childItemType.includes('missile') || childItemType.includes('torpedo') || childItemType.includes('bomb'))) {
                const stockDamage = childItemDef.missileStats?.damage || 0;
                stockTotalDamage += stockDamage;
                
                // Current damage - use rack override if present
                const currentDamage = rackOverride?.missileStats?.damage || stockDamage;
                currentTotalDamage += currentDamage;
                missileCount++;
                
                // Group by missile type
                const key = rackOverride ? rackOverride.itemName : childItemDef.itemName;
                const existing = missileGroups.get(key);
                if (existing) {
                  existing.count++;
                } else {
                  missileGroups.set(key, {
                    itemDef: childItemDef,
                    override: rackOverride,
                    count: 1
                  });
                }
              }
            });
          }
        });
      }
    });

    // Convert grouped missiles to display array
    missileGroups.forEach(({ itemDef, override, count }) => {
      const current = override || itemDef;
      const stats = current.missileStats || itemDef.missileStats;
      
      missiles.push({
        name: current.localizedName || current.itemName,
        manufacturer: current.manufacturer || 'Unknown',
        size: current.size || 1,
        grade: current.grade || 1,
        type: current.type || 'Missile',
        damage: stats?.damage || 0,
        speed: stats?.speed || 0,
        lockTime: stats?.lockTime || 0,
        launchDelay: stats?.launchDelay || 0,
        lockingRange: stats?.lockingRange || 0,
        isModified: !!override,
        quantity: count
      });
    });

    if (missileCount === 0) return null;

    return {
      count: missileCount,
      totalDamage: {
        current: currentTotalDamage,
        stock: stockTotalDamage,
        delta: currentTotalDamage - stockTotalDamage,
        improved: currentTotalDamage > stockTotalDamage
      },
      missiles
    };
  }, [hardpoints, hardpointOverrides, loadedItemOverrides]);

  // Calculate radar stats
  const radarStats = useMemo(() => {
    const radarHardpoint = hardpoints.find(hp => 
      hp.hardpointType === 'Radar' ||
      hp.itemDefinition?.type?.toLowerCase() === 'radar'
    );

    if (!radarHardpoint) return null;

    const override = hardpointOverrides.get(radarHardpoint.id);
    const stockRadar = radarHardpoint.itemDefinition;
    const currentRadar = override || stockRadar;

    if (!currentRadar && !stockRadar) return null;

    // Get radar name
    const currentName = currentRadar?.localizedName || currentRadar?.itemName || 'Unknown';
    const stockName = stockRadar?.localizedName || stockRadar?.itemName || 'Unknown';
    
    // Get manufacturer, size, grade
    const manufacturer = currentRadar?.manufacturer || stockRadar?.manufacturer || 'Unknown';
    const currentSize = currentRadar?.size || stockRadar?.size || 1;
    const stockSize = stockRadar?.size || 1;
    const grade = currentRadar?.grade || stockRadar?.grade || 3;

    // Get health  
    const currentHealth = currentRadar?.health || 0;
    const stockHealth = stockRadar?.health || 0;

    // Get power pips
    const currentPips = currentRadar?.powerPipStats?.powerUnits || 3;
    const stockPips = stockRadar?.powerPipStats?.powerUnits || 3;

    // Get radar-specific stats
    const currentSensitivity = currentRadar?.radarStats?.sensitivity || 0;
    const stockSensitivity = stockRadar?.radarStats?.sensitivity || 0;
    const currentPiercing = currentRadar?.radarStats?.piercing || 0;
    const stockPiercing = stockRadar?.radarStats?.piercing || 0;

    return {
      name: currentName,
      stockName,
      manufacturer,
      size: currentSize,
      grade,
      isModified: !!override,
      health: {
        current: currentHealth,
        stock: stockHealth,
        delta: currentHealth - stockHealth,
        improved: currentHealth > stockHealth
      },
      pips: {
        current: currentPips,
        stock: stockPips,
        delta: currentPips - stockPips,
        improved: currentPips < stockPips // Lower power draw is better
      },
      sensitivity: {
        current: currentSensitivity,
        stock: stockSensitivity,
        delta: currentSensitivity - stockSensitivity,
        improved: currentSensitivity > stockSensitivity // Higher is better
      },
      piercing: {
        current: currentPiercing,
        stock: stockPiercing,
        delta: currentPiercing - stockPiercing,
        improved: currentPiercing > stockPiercing // Higher is better
      }
    };
  }, [hardpoints, hardpointOverrides]);

  // Calculate utility equipment stats (tractor beams, mining, salvage)
  const utilityStats = useMemo(() => {
    const tractorBeams: {
      name: string;
      stockName: string;
      size: number;
      pips: StatChange;
      health: StatChange;
      isModified: boolean;
      hardpointId: number;
    }[] = [];
    
    const miningEquipment: {
      name: string;
      stockName: string;
      size: number;
      pips: StatChange;
      health: StatChange;
      isModified: boolean;
      hardpointId: number;
      // Mining laser stats
      power?: StatChange;
      resistance?: StatChange;
      instability?: StatChange;
      range?: StatChange;
    }[] = [];
    
    const salvageEquipment: {
      name: string;
      stockName: string;
      size: number;
      pips: StatChange;
      health: StatChange;
      isModified: boolean;
      hardpointId: number;
    }[] = [];

    hardpoints.forEach(hp => {
      const slotName = (hp.slotName || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      const defaultName = (hp.defaultItemName || '').toLowerCase();
      
      // Skip controllers
      if (slotName.includes('controller') || defaultName.includes('controller')) return;

      const override = hardpointOverrides.get(hp.id);
      const stockItem = hp.itemDefinition;
      const currentItem = override || stockItem;

      // Tractor beams - can be type 'tractorbeam' or 'toolarm' in a tractor slot (SRV)
      const isTractorSlot = slotName.includes('tractor') || defaultName.includes('tractor');
      const isTractorItem = itemType === 'tractorbeam';
      const isToolArmInTractorSlot = itemType === 'toolarm' && isTractorSlot;
      
      if (isTractorItem || isTractorSlot) {
        // Accept tractorbeam type OR toolarm in a tractor slot
        if (currentItem && (currentItem.type?.toLowerCase() === 'tractorbeam' || isTractorItem || isToolArmInTractorSlot)) {
          const currentPips = currentItem?.powerPipStats?.powerUnits || 1;
          const stockPips = stockItem?.powerPipStats?.powerUnits || 1;
          const currentHealth = currentItem?.health || 0;
          const stockHealth = stockItem?.health || 0;

          // Fix incorrect "Mining Arm" names for tractor equipment
          let tractorName = currentItem?.localizedName || currentItem?.itemName || 'Tractor Beam';
          let stockTractorName = stockItem?.localizedName || stockItem?.itemName || 'Tractor Beam';
          if (tractorName.toLowerCase().includes('mining') && isTractorSlot) {
            tractorName = 'Tractor Beam Arm';
          }
          if (stockTractorName.toLowerCase().includes('mining') && isTractorSlot) {
            stockTractorName = 'Tractor Beam Arm';
          }

          tractorBeams.push({
            name: tractorName,
            stockName: stockTractorName,
            size: currentItem?.size || stockItem?.size || 1,
            pips: { current: currentPips, stock: stockPips, delta: currentPips - stockPips, improved: currentPips < stockPips },
            health: { current: currentHealth, stock: stockHealth, delta: currentHealth - stockHealth, improved: currentHealth > stockHealth },
            isModified: !!override,
            hardpointId: hp.id
          });
        }
      }

      // Mining equipment (WeaponMining, ToolArm, UtilityTurret with mining)
      // IMPORTANT: Exclude salvage slots - ToolArm is used for both mining AND salvage
      // IMPORTANT: Exclude tractor slots - ToolArm is also used for tractor beams (SRV)
      // IMPORTANT: Exclude ore pods - they are storage containers, not mining equipment (no pips)
      const isSalvageSlotCheck = slotName.includes('salvage') || defaultName.includes('salvage');
      const isTractorSlotCheck = slotName.includes('tractor') || defaultName.includes('tractor');
      const isOrePod = slotName.includes('pod') || defaultName.includes('pod') || itemType === 'container';
      const isMiningItem = itemType === 'weaponmining' || (itemType === 'toolarm' && !isSalvageSlotCheck && !isTractorSlotCheck);
      const isMiningSlot = (slotName.includes('mining') || defaultName.includes('mining')) && !isSalvageSlotCheck && !isTractorSlotCheck && !isOrePod;
      const isUtilityTurret = itemType === 'utilityturret' && (slotName.includes('mining') || defaultName.includes('mining')) && !isSalvageSlotCheck && !isTractorSlotCheck && !isOrePod;
      
      if ((isMiningItem || isMiningSlot || isUtilityTurret) && !isOrePod) {
        // For turrets, try to find actual mining item in loadedItems
        let miningItem = currentItem;
        let stockMiningItem = stockItem;
        
        if (hp.loadedItems && hp.loadedItems.length > 0) {
          const findMiningItem = (items: any[]): any | null => {
            for (const item of items) {
              const type = (item.itemDefinition?.type || '').toLowerCase();
              if (type === 'weaponmining') return item.itemDefinition;
              if (item.childAttachments?.length > 0) {
                const found = findMiningItem(item.childAttachments);
                if (found) return found;
              }
            }
            return null;
          };
          const found = findMiningItem(hp.loadedItems as any[]);
          if (found) {
            miningItem = found;
            stockMiningItem = found;
          }
        }

        if (miningItem) {
          const currentPips = miningItem?.powerPipStats?.powerUnits || 1;
          const stockPips = stockMiningItem?.powerPipStats?.powerUnits || 1;
          const currentHealth = miningItem?.health || 0;
          const stockHealth = stockMiningItem?.health || 0;

          // Mining laser stats
          // Base mining laser stats
          const basePower = miningItem?.miningLaserStats?.power || 0;
          const baseResistance = miningItem?.miningLaserStats?.resistance || 0;
          const baseInstability = miningItem?.miningLaserStats?.instability || 0;
          const baseWindow = miningItem?.miningLaserStats?.optimalWindow || 0;
          const baseRange = miningItem?.miningLaserStats?.range || 0;
          
          const stockPower = stockMiningItem?.miningLaserStats?.power || 0;
          const stockResistance = stockMiningItem?.miningLaserStats?.resistance || 0;
          const stockInstability = stockMiningItem?.miningLaserStats?.instability || 0;
          const stockRange = stockMiningItem?.miningLaserStats?.range || 0;
          
          // Calculate module-modified stats
          let currentPower = basePower;
          let currentResistance = baseResistance;
          let currentInstability = baseInstability;
          let currentWindow = baseWindow;
          const currentRange = baseRange;
          
          // Apply module modifiers from childAttachments
          if (hp.loadedItems && hp.loadedItems.length > 0) {
            const findModules = (items: any[]): any[] => {
              const modules: any[] = [];
              for (const item of items) {
                const type = (item.itemDefinition?.type || '').toLowerCase();
                if (type === 'miningmodifier') {
                  modules.push(item.itemDefinition);
                }
                if (item.childAttachments?.length > 0) {
                  modules.push(...findModules(item.childAttachments));
                }
              }
              return modules;
            };
            
            const modules = findModules(hp.loadedItems as any[]);
            modules.forEach(mod => {
              const desc = mod.description || '';
              const statPattern = /([A-Za-z\s]+):\s*([+-]?\d+\.?\d*)%/g;
              let match;
              while ((match = statPattern.exec(desc)) !== null) {
                const label = match[1].trim().toLowerCase();
                const value = parseFloat(match[2]);
                
                if (label.includes('mining laser power') || (label.includes('laser') && label.includes('power') && !label.includes('extraction'))) {
                  currentPower *= (value / 100);
                } else if (label.includes('resistance')) {
                  currentResistance += baseResistance * (value / 100);
                } else if (label.includes('instability')) {
                  if (match[2].startsWith('+') || match[2].startsWith('-')) {
                    currentInstability += baseInstability * (value / 100);
                  } else {
                    currentInstability *= (value / 100);
                  }
                } else if (label.includes('optimal') && label.includes('window')) {
                  currentWindow += baseWindow * (value / 100);
                }
              }
            });
          }

          miningEquipment.push({
            name: miningItem?.localizedName || miningItem?.itemName || 'Mining Laser',
            stockName: stockMiningItem?.localizedName || stockMiningItem?.itemName || 'Mining Laser',
            size: miningItem?.size || stockMiningItem?.size || 1,
            pips: { current: currentPips, stock: stockPips, delta: currentPips - stockPips, improved: currentPips < stockPips },
            health: { current: currentHealth, stock: stockHealth, delta: currentHealth - stockHealth, improved: currentHealth > stockHealth },
            isModified: !!override,
            hardpointId: hp.id,
            // Mining laser stats
            power: currentPower > 0 ? { current: currentPower, stock: stockPower, delta: currentPower - stockPower, improved: currentPower > stockPower } : undefined,
            resistance: currentResistance > 0 ? { current: currentResistance, stock: stockResistance, delta: currentResistance - stockResistance, improved: currentResistance > stockResistance } : undefined,
            instability: currentInstability > 0 ? { current: currentInstability, stock: stockInstability, delta: currentInstability - stockInstability, improved: currentInstability > stockInstability } : undefined,
            range: currentRange > 0 ? { current: currentRange, stock: stockRange, delta: currentRange - stockRange, improved: currentRange > stockRange } : undefined
          });
        }
      }

      // Salvage equipment (SalvageHead, ToolArm with salvage slot)
      // IMPORTANT: Exclude internal salvage components that don't consume pips
      const isInternalSalvage = defaultName.includes('filler') || defaultName.includes('internalstorage') ||
                                defaultName.includes('disintegration') || defaultName.includes('fieldsupporter') ||
                                defaultName.includes('buff_modifier') || itemType === 'container';
      const isSalvageItem = (itemType.includes('salvage') || (itemType === 'toolarm' && (slotName.includes('salvage') || defaultName.includes('salvage')))) && !isInternalSalvage;
      const isSalvageSlot = (slotName.includes('salvage') || defaultName.includes('salvage')) && slotName.includes('arm') && !isInternalSalvage;
      
      if ((isSalvageItem || isSalvageSlot) && !isInternalSalvage) {
        if (currentItem) {
          const currentPips = currentItem?.powerPipStats?.powerUnits || 1;
          const stockPips = stockItem?.powerPipStats?.powerUnits || 1;
          const currentHealth = currentItem?.health || 0;
          const stockHealth = stockItem?.health || 0;

          // Generate proper display name for salvage equipment
          let salvageName = currentItem?.localizedName || '';
          let stockSalvageName = stockItem?.localizedName || '';
          if (!salvageName || salvageName === 'Unknown Item') {
            if (slotName.includes('left')) salvageName = 'Salvage Arm (Left)';
            else if (slotName.includes('right')) salvageName = 'Salvage Arm (Right)';
            else salvageName = 'Salvage Arm';
          }
          if (!stockSalvageName || stockSalvageName === 'Unknown Item') {
            if (slotName.includes('left')) stockSalvageName = 'Salvage Arm (Left)';
            else if (slotName.includes('right')) stockSalvageName = 'Salvage Arm (Right)';
            else stockSalvageName = 'Salvage Arm';
          }

          salvageEquipment.push({
            name: salvageName,
            stockName: stockSalvageName,
            size: currentItem?.size || stockItem?.size || 1,
            pips: { current: currentPips, stock: stockPips, delta: currentPips - stockPips, improved: currentPips < stockPips },
            health: { current: currentHealth, stock: stockHealth, delta: currentHealth - stockHealth, improved: currentHealth > stockHealth },
            isModified: !!override,
            hardpointId: hp.id
          });
        }
      }
    });

    // Return null if no utility equipment found
    if (tractorBeams.length === 0 && miningEquipment.length === 0 && salvageEquipment.length === 0) {
      return null;
    }

    // Calculate totals
    const totalTractorPips = tractorBeams.reduce((sum, t) => sum + t.pips.current, 0);
    const stockTractorPips = tractorBeams.reduce((sum, t) => sum + t.pips.stock, 0);
    const totalMiningPips = miningEquipment.reduce((sum, m) => sum + m.pips.current, 0);
    const stockMiningPips = miningEquipment.reduce((sum, m) => sum + m.pips.stock, 0);
    const totalSalvagePips = salvageEquipment.reduce((sum, s) => sum + s.pips.current, 0);
    const stockSalvagePips = salvageEquipment.reduce((sum, s) => sum + s.pips.stock, 0);

    // Calculate aggregate mining stats (sum of all mining lasers)
    const totalMiningPower = miningEquipment.reduce((sum, m) => sum + (m.power?.current || 0), 0);
    const stockMiningPower = miningEquipment.reduce((sum, m) => sum + (m.power?.stock || 0), 0);
    const avgMiningResistance = miningEquipment.length > 0 
      ? miningEquipment.reduce((sum, m) => sum + (m.resistance?.current || 0), 0) / miningEquipment.filter(m => m.resistance).length
      : 0;
    const stockAvgMiningResistance = miningEquipment.length > 0 
      ? miningEquipment.reduce((sum, m) => sum + (m.resistance?.stock || 0), 0) / miningEquipment.filter(m => m.resistance).length
      : 0;
    const avgMiningInstability = miningEquipment.length > 0 
      ? miningEquipment.reduce((sum, m) => sum + (m.instability?.current || 0), 0) / miningEquipment.filter(m => m.instability).length
      : 0;
    const stockAvgMiningInstability = miningEquipment.length > 0 
      ? miningEquipment.reduce((sum, m) => sum + (m.instability?.stock || 0), 0) / miningEquipment.filter(m => m.instability).length
      : 0;
    const avgMiningRange = miningEquipment.length > 0 
      ? miningEquipment.reduce((sum, m) => sum + (m.range?.current || 0), 0) / miningEquipment.filter(m => m.range).length
      : 0;
    const stockAvgMiningRange = miningEquipment.length > 0 
      ? miningEquipment.reduce((sum, m) => sum + (m.range?.stock || 0), 0) / miningEquipment.filter(m => m.range).length
      : 0;

    return {
      tractorBeams,
      miningEquipment,
      salvageEquipment,
      totalPips: {
        tractor: { current: totalTractorPips, stock: stockTractorPips, delta: totalTractorPips - stockTractorPips, improved: totalTractorPips < stockTractorPips },
        mining: { current: totalMiningPips, stock: stockMiningPips, delta: totalMiningPips - stockMiningPips, improved: totalMiningPips < stockMiningPips },
        salvage: { current: totalSalvagePips, stock: stockSalvagePips, delta: totalSalvagePips - stockSalvagePips, improved: totalSalvagePips < stockSalvagePips }
      },
      // Aggregate mining stats
      aggregateMiningStats: miningEquipment.length > 0 ? {
        totalPower: totalMiningPower > 0 ? { current: totalMiningPower, stock: stockMiningPower, delta: totalMiningPower - stockMiningPower, improved: totalMiningPower > stockMiningPower } : undefined,
        avgResistance: avgMiningResistance > 0 ? { current: avgMiningResistance, stock: stockAvgMiningResistance, delta: avgMiningResistance - stockAvgMiningResistance, improved: avgMiningResistance > stockAvgMiningResistance } : undefined,
        avgInstability: avgMiningInstability > 0 ? { current: avgMiningInstability, stock: stockAvgMiningInstability, delta: avgMiningInstability - stockAvgMiningInstability, improved: avgMiningInstability > stockAvgMiningInstability } : undefined,
        avgRange: avgMiningRange > 0 ? { current: avgMiningRange, stock: stockAvgMiningRange, delta: avgMiningRange - stockAvgMiningRange, improved: avgMiningRange > stockAvgMiningRange } : undefined
      } : undefined,
      hasModifications: tractorBeams.some(t => t.isModified) || miningEquipment.some(m => m.isModified) || salvageEquipment.some(s => s.isModified)
    };
  }, [hardpoints, hardpointOverrides]);

  const formatNumber = (num: number, decimals: number = 0) => {
    const absNum = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    if (absNum >= 1000000) return sign + (absNum / 1000000).toFixed(1) + 'M';
    if (absNum >= 1000) return sign + (absNum / 1000).toFixed(1) + 'k';
    return num.toFixed(decimals);
  };

  const formatDelta = (delta: number, decimals: number = 0, invert: boolean = false) => {
    if (delta === 0) return null;
    const improved = invert ? delta < 0 : delta > 0;
    const sign = delta > 0 ? '+' : '';
    return (
      <span className={improved ? styles.deltaPositive : styles.deltaNegative}>
        {sign}{formatNumber(delta, decimals)}
      </span>
    );
  };

  const hasChanges = hardpointOverrides.size > 0;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h3 className={styles.title}>Ship Stats</h3>
        {hasChanges && <span className={styles.modifiedBadge}>Modified</span>}
      </div>

      {/* Shield Stats */}
      {shieldStats.count > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Shield size={16} />
            <span>Shields</span>
            <span className={styles.count}>×{shieldStats.count}</span>
          </div>
          <div className={styles.statRow}>
            <span className={styles.statLabel}>Total HP</span>
            <span className={styles.statValue}>
              {formatNumber(shieldStats.hp.current)}
              {formatDelta(shieldStats.hp.delta)}
            </span>
          </div>
          <div className={styles.statRow}>
            <span className={styles.statLabel}>Regen</span>
            <span className={styles.statValue}>
              {formatNumber(shieldStats.regen.current)}/s
              {formatDelta(shieldStats.regen.delta)}
            </span>
          </div>
          
          {/* Individual Shields */}
          {shieldStats.shields.map((shield, idx) => (
            <div key={idx} className={styles.componentDetail}>
              <div className={styles.componentHeader}>
                <span className={styles.componentName}>
                  {shield.name}
                  {shield.isModified && <span className={styles.modifiedBadge}>Modified</span>}
                </span>
              </div>
              <div className={styles.componentMeta}>
                <span className={styles.metaItem}>{shield.manufacturer}</span>
                <span className={styles.metaItem}>S{shield.size}</span>
                <span className={styles.metaItem}>Grade {['A', 'B', 'C', 'D', 'F'][shield.grade - 1] || 'C'}</span>
              </div>
              <div className={styles.componentStats}>
                {shield.hp.current > 0 && (
                  <span className={styles.componentStat}>
                    {formatNumber(shield.hp.current)} HP
                    {shield.hp.delta !== 0 && formatDelta(shield.hp.delta)}
                  </span>
                )}
                {shield.regen.current > 0 && (
                  <span className={styles.componentStat}>
                    {formatNumber(shield.regen.current)}/s regen
                    {shield.regen.delta !== 0 && formatDelta(shield.regen.delta)}
                  </span>
                )}
                {shield.downedDelay.current > 0 && (
                  <span className={styles.componentStat}>
                    {shield.downedDelay.current.toFixed(1)}s downed
                    {shield.downedDelay.delta !== 0 && formatDelta(shield.downedDelay.delta, 1, true)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Power Stats */}
      {powerStats.count > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Zap size={16} />
            <span>Power</span>
            <span className={styles.count}>×{powerStats.count}</span>
          </div>
          <div className={styles.statRow}>
            <span className={styles.statLabel}>Total Pips</span>
            <span className={styles.statValue}>
              {powerStats.pips.current}
              {formatDelta(powerStats.pips.delta)}
            </span>
          </div>
          {powerStats.output.current > 0 && (
            <div className={styles.statRow}>
              <span className={styles.statLabel}>Output</span>
              <span className={styles.statValue}>
                {formatNumber(powerStats.output.current, 0)} pips
                {formatDelta(powerStats.output.delta)}
              </span>
            </div>
          )}
          
          {/* Individual Power Plants */}
          {powerStats.plants.map((plant, idx) => (
            <div key={idx} className={styles.componentDetail}>
              <div className={styles.componentHeader}>
                <span className={styles.componentName}>
                  {plant.name}
                  {plant.isModified && <span className={styles.modifiedBadge}>Modified</span>}
                </span>
              </div>
              <div className={styles.componentMeta}>
                <span className={styles.metaItem}>{plant.manufacturer}</span>
                <span className={styles.metaItem}>S{plant.size}</span>
                <span className={styles.metaItem}>Grade {['A', 'B', 'C', 'D', 'F'][plant.grade - 1] || 'C'}</span>
              </div>
              <div className={styles.componentStats}>
                <span className={styles.componentStat}>
                  {plant.pips.current} pips
                  {plant.pips.delta !== 0 && formatDelta(plant.pips.delta)}
                </span>
                {plant.output.current > 0 && (
                  <span className={styles.componentStat}>
                    {formatNumber(plant.output.current, 0)} pips
                    {plant.output.delta !== 0 && formatDelta(plant.output.delta)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cooler Stats */}
      {coolerStats.count > 0 && coolerStats.cooling.current > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Thermometer size={16} />
            <span>Cooling</span>
            <span className={styles.count}>×{coolerStats.count}</span>
          </div>
          <div className={styles.statRow}>
            <span className={styles.statLabel}>Total Rate</span>
            <span className={styles.statValue}>
              {formatNumber(coolerStats.cooling.current)}/s
              {formatDelta(coolerStats.cooling.delta)}
            </span>
          </div>
          
          {/* Individual cooler details */}
          {coolerStats.coolers.map((cooler, index) => (
            <div key={index} className={styles.componentDetail}>
              <div className={styles.componentHeader}>
                <span className={styles.componentName}>{cooler.name}</span>
                {cooler.isModified && <span className={styles.modifiedBadge}>Modified</span>}
              </div>
              <div className={styles.componentMeta}>
                <span className={styles.metaItem}>{cooler.manufacturer}</span>
                <span className={styles.metaItem}>S{cooler.size}</span>
                <span className={styles.metaItem}>Grade {['A', 'B', 'C', 'D'][cooler.grade - 1] || 'C'}</span>
              </div>
              <div className={styles.componentStats}>
                <div className={styles.componentStat}>
                  <span>Cooling:</span>
                  <span>{formatNumber(cooler.coolingRate)}/s</span>
                </div>
                <div className={styles.componentStat}>
                  <span>Power:</span>
                  <span>{cooler.pips} pips</span>
                </div>
                {cooler.health > 0 && (
                  <div className={styles.componentStat}>
                    <span>HP:</span>
                    <span>{cooler.health >= 1000 ? `${(cooler.health / 1000).toFixed(1)}k` : cooler.health}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quantum Drive Stats */}
      {quantumStats && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Gauge size={16} />
            <span>Quantum</span>
          </div>
          
          {/* Component details */}
          <div className={styles.componentDetail}>
            <div className={styles.componentHeader}>
              <span className={styles.componentName}>{quantumStats.name}</span>
              {quantumStats.isModified && <span className={styles.modifiedBadge}>Modified</span>}
            </div>
            <div className={styles.componentMeta}>
              <span className={styles.metaItem}>{quantumStats.manufacturer}</span>
              <span className={styles.metaItem}>S{quantumStats.size}</span>
              <span className={styles.metaItem}>Grade {['A', 'B', 'C', 'D'][quantumStats.grade - 1] || 'C'}</span>
            </div>
            <div className={styles.componentStats}>
              {quantumStats.speed.current > 0 && (
                <div className={styles.componentStat}>
                  <span>Speed:</span>
                  <span>{formatNumber(quantumStats.speed.current / 1000000, 1)}Mm/s</span>
                </div>
              )}
              {quantumStats.spool.current > 0 && (
                <div className={styles.componentStat}>
                  <span>Spool:</span>
                  <span>{quantumStats.spool.current.toFixed(1)}s</span>
                </div>
              )}
              {quantumStats.cooldown.current > 0 && (
                <div className={styles.componentStat}>
                  <span>Cooldown:</span>
                  <span>{quantumStats.cooldown.current.toFixed(1)}s</span>
                </div>
              )}
              <div className={styles.componentStat}>
                <span>Power:</span>
                <span>{quantumStats.pips} pips</span>
              </div>
              {quantumStats.health > 0 && (
                <div className={styles.componentStat}>
                  <span>HP:</span>
                  <span>{quantumStats.health >= 1000 ? `${(quantumStats.health / 1000).toFixed(1)}k` : quantumStats.health}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Weapons Stats */}
      {weaponStats.count > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Crosshair size={16} />
            <span>Weapons</span>
            <span className={styles.count}>×{weaponStats.count}</span>
          </div>
          {weaponStats.totalDPS.current > 0 ? (
            <>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Total DPS</span>
                <span className={styles.statValue}>
                  {formatNumber(weaponStats.totalDPS.current, 1)}
                  {formatDelta(weaponStats.totalDPS.delta, 1)}
                </span>
              </div>
              {weaponStats.ballisticDPS.current > 0 && (
                <div className={styles.statRow}>
                  <span className={styles.statLabel} style={{ color: '#ffa500' }}>Ballistic</span>
                  <span className={styles.statValue}>
                    {formatNumber(weaponStats.ballisticDPS.current, 1)}
                    {formatDelta(weaponStats.ballisticDPS.delta, 1)}
                  </span>
                </div>
              )}
              {weaponStats.energyDPS.current > 0 && (
                <div className={styles.statRow}>
                  <span className={styles.statLabel} style={{ color: '#00bfff' }}>Energy</span>
                  <span className={styles.statValue}>
                    {formatNumber(weaponStats.energyDPS.current, 1)}
                    {formatDelta(weaponStats.energyDPS.delta, 1)}
                  </span>
                </div>
              )}
              {weaponStats.distortionDPS.current > 0 && (
                <div className={styles.statRow}>
                  <span className={styles.statLabel} style={{ color: '#9966ff' }}>Distortion</span>
                  <span className={styles.statValue}>
                    {formatNumber(weaponStats.distortionDPS.current, 1)}
                    {formatDelta(weaponStats.distortionDPS.delta, 1)}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className={styles.statRow}>
              <span className={styles.statLabel}>Equipped</span>
              <span className={styles.statValue}>{weaponStats.count} guns</span>
            </div>
          )}
          
          {/* Individual weapon details */}
          {weaponStats.weapons.map((weapon, index) => {
            const damageTypeColors: Record<string, string> = {
              ballistic: '#ffa500',
              energy: '#00bfff',
              distortion: '#9966ff',
              unknown: '#888888'
            };
            
            return (
              <div key={index} className={styles.componentDetail}>
                <div className={styles.componentHeader}>
                  <span className={styles.componentName}>
                    {weapon.name} {weapon.quantity > 1 && `×${weapon.quantity}`}
                  </span>
                  {weapon.isModified && <span className={styles.modifiedBadge}>Modified</span>}
                </div>
                <div className={styles.componentMeta}>
                  <span className={styles.metaItem}>{weapon.manufacturer}</span>
                  <span className={styles.metaItem}>S{weapon.size}</span>
                  <span className={styles.metaItem}>Grade {['A', 'B', 'C', 'D'][weapon.grade - 1] || '?'}</span>
                  <span className={styles.metaItem} style={{ color: damageTypeColors[weapon.damageType] }}>
                    {weapon.damageType.charAt(0).toUpperCase() + weapon.damageType.slice(1)}
                  </span>
                </div>
                <div className={styles.componentStats}>
                  {weapon.dps > 0 && (
                    <div className={styles.componentStat}>
                      <span>DPS:</span>
                      <span>{formatNumber(weapon.dps, 1)}</span>
                    </div>
                  )}
                  {weapon.fireRate > 0 && (
                    <div className={styles.componentStat}>
                      <span>Fire Rate:</span>
                      <span>{formatNumber(weapon.fireRate, 0)} rpm</span>
                    </div>
                  )}
                  {weapon.damagePerShot > 0 && (
                    <div className={styles.componentStat}>
                      <span>Damage/Shot:</span>
                      <span>{formatNumber(weapon.damagePerShot, 1)}</span>
                    </div>
                  )}
                  {weapon.range > 0 && (
                    <div className={styles.componentStat}>
                      <span>Range:</span>
                      <span>{formatNumber(weapon.range / 1000, 1)}km</span>
                    </div>
                  )}
                  {weapon.velocity > 0 && (
                    <div className={styles.componentStat}>
                      <span>Velocity:</span>
                      <span>{formatNumber(weapon.velocity, 0)} m/s</span>
                    </div>
                  )}
                  {weapon.ammoCapacity > 0 && (
                    <div className={styles.componentStat}>
                      <span>Ammo:</span>
                      <span>{formatNumber(weapon.ammoCapacity, 0)}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Missile Stats */}
      {missileStats && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Rocket size={16} />
            <span>Missiles</span>
            <span className={styles.count}>×{missileStats.count}</span>
          </div>
          <div className={styles.statRow}>
            <span className={styles.statLabel}>Total Damage</span>
            <span className={styles.statValue}>
              {formatNumber(missileStats.totalDamage.current, 0)}
              {formatDelta(missileStats.totalDamage.delta, 0)}
            </span>
          </div>
          
          {/* Individual missile details */}
          {missileStats.missiles.map((missile, index) => (
            <div key={index} className={styles.componentDetail}>
              <div className={styles.componentHeader}>
                <span className={styles.componentName}>
                  {missile.name} {missile.quantity > 1 && `×${missile.quantity}`}
                </span>
                {missile.isModified && <span className={styles.modifiedBadge}>Modified</span>}
              </div>
              <div className={styles.componentMeta}>
                <span className={styles.metaItem}>{missile.manufacturer}</span>
                <span className={styles.metaItem}>S{missile.size}</span>
                <span className={styles.metaItem}>Grade {['A', 'B', 'C', 'D'][missile.grade - 1] || '?'}</span>
              </div>
              <div className={styles.componentStats}>
                {missile.damage > 0 && (
                  <div className={styles.componentStat}>
                    <span>Damage:</span>
                    <span>{formatNumber(missile.damage, 0)}</span>
                  </div>
                )}
                {missile.speed > 0 && (
                  <div className={styles.componentStat}>
                    <span>Speed:</span>
                    <span>{formatNumber(missile.speed, 0)} m/s</span>
                  </div>
                )}
                {missile.lockTime > 0 && (
                  <div className={styles.componentStat}>
                    <span>Lock Time:</span>
                    <span>{missile.lockTime.toFixed(1)}s</span>
                  </div>
                )}
                {missile.launchDelay > 0 && (
                  <div className={styles.componentStat}>
                    <span>Launch Delay:</span>
                    <span>{missile.launchDelay.toFixed(2)}s</span>
                  </div>
                )}
                {missile.lockingRange > 0 && (
                  <div className={styles.componentStat}>
                    <span>Lock Range:</span>
                    <span>{formatNumber(missile.lockingRange / 1000, 1)}km</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Radar Stats */}
      {radarStats && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Radio size={16} />
            <span>Radar</span>
            {radarStats.isModified && <span className={styles.modifiedBadge}>Modified</span>}
          </div>
          
          {/* Component Details */}
          <div className={styles.componentDetail}>
            <div className={styles.componentHeader}>
              <span className={styles.componentName}>{radarStats.name}</span>
            </div>
            <div className={styles.componentMeta}>
              <span className={styles.metaItem}>{radarStats.manufacturer}</span>
              <span className={styles.metaItem}>S{radarStats.size}</span>
              <span className={styles.metaItem}>Grade {['A', 'B', 'C', 'D', 'F'][radarStats.grade - 1] || 'C'}</span>
            </div>
            <div className={styles.componentStats}>
              {radarStats.sensitivity.current > 0 && (
                <span className={styles.componentStat}>
                  {(radarStats.sensitivity.current * 100).toFixed(0)}% sensitivity
                  {radarStats.sensitivity.delta !== 0 && formatDelta(radarStats.sensitivity.delta * 100, 0)}
                </span>
              )}
              {radarStats.piercing.current > 0 && (
                <span className={styles.componentStat}>
                  {(radarStats.piercing.current * 100).toFixed(0)}% piercing
                  {radarStats.piercing.delta !== 0 && formatDelta(radarStats.piercing.delta * 100, 0)}
                </span>
              )}
              <span className={styles.componentStat}>
                {radarStats.pips.current} pips
                {radarStats.pips.delta !== 0 && formatDelta(radarStats.pips.delta, 0, true)}
              </span>
              {radarStats.health.current > 0 && (
                <span className={styles.componentStat}>
                  {formatNumber(radarStats.health.current)} HP
                  {radarStats.health.delta !== 0 && formatDelta(radarStats.health.delta)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Utility Equipment Stats */}
      {utilityStats && (
        <>
          {/* Tractor Beams */}
          {utilityStats.tractorBeams.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <Magnet size={16} />
                <span>Tractor Beams ({utilityStats.tractorBeams.length})</span>
                {utilityStats.tractorBeams.some(t => t.isModified) && <span className={styles.modifiedBadge}>Modified</span>}
              </div>
              {utilityStats.tractorBeams.map((tractor, idx) => (
                <div key={tractor.hardpointId} className={styles.subItem}>
                  <div className={styles.statRow}>
                    <span className={styles.statLabel}>Model</span>
                    <span className={styles.statValue}>{tractor.name}</span>
                  </div>
                  <div className={styles.statRow}>
                    <span className={styles.statLabel}>Size</span>
                    <span className={styles.statValue}>S{tractor.size}</span>
                  </div>
                  <div className={styles.statRow}>
                    <span className={styles.statLabel}>Power</span>
                    <span className={styles.statValue}>
                      {tractor.pips.current} pips
                      {formatDelta(tractor.pips.delta, 0, true)}
                    </span>
                  </div>
                </div>
              ))}
              {utilityStats.tractorBeams.length > 1 && (
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Total Power</span>
                  <span className={styles.statValue}>
                    {utilityStats.totalPips.tractor.current} pips
                    {formatDelta(utilityStats.totalPips.tractor.delta, 0, true)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Mining Equipment */}
          {utilityStats.miningEquipment.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <Pickaxe size={16} />
                <span>Mining ({utilityStats.miningEquipment.length})</span>
                {utilityStats.miningEquipment.some(m => m.isModified) && <span className={styles.modifiedBadge}>Modified</span>}
              </div>
              {utilityStats.miningEquipment.map((mining, idx) => (
                <div key={mining.hardpointId} className={styles.subItem}>
                  <div className={styles.statRow}>
                    <span className={styles.statLabel}>Model</span>
                    <span className={styles.statValue}>{mining.name}</span>
                  </div>
                  <div className={styles.statRow}>
                    <span className={styles.statLabel}>Size</span>
                    <span className={styles.statValue}>S{mining.size}</span>
                  </div>
                  <div className={styles.statRow}>
                    <span className={styles.statLabel}>Power</span>
                    <span className={styles.statValue}>
                      {mining.pips.current} pips
                      {formatDelta(mining.pips.delta, 0, true)}
                    </span>
                  </div>
                  {mining.power && (
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Mining Power</span>
                      <span className={styles.statValue}>
                        {formatNumber(mining.power.current)}
                        {formatDelta(mining.power.delta)}
                      </span>
                    </div>
                  )}
                  {mining.resistance && (
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Resistance</span>
                      <span className={styles.statValue}>
                        {mining.resistance.current.toFixed(1)}
                        {formatDelta(mining.resistance.delta, 1)}
                      </span>
                    </div>
                  )}
                  {mining.instability && (
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Instability</span>
                      <span className={styles.statValue}>
                        {mining.instability.current.toFixed(1)}
                        {formatDelta(mining.instability.delta, 1)}
                      </span>
                    </div>
                  )}
                  {mining.range && mining.range.current > 100 && (
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Range</span>
                      <span className={styles.statValue}>
                        {(mining.range.current / 1000).toFixed(2)}km
                        {mining.range.delta !== 0 && formatDelta(mining.range.delta / 1000, 2)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              {utilityStats.miningEquipment.length > 1 && (
                <>
                  <div className={styles.statRow}>
                    <span className={styles.statLabel}>Total Power</span>
                    <span className={styles.statValue}>
                      {utilityStats.totalPips.mining.current} pips
                      {formatDelta(utilityStats.totalPips.mining.delta, 0, true)}
                    </span>
                  </div>
                  {/* Show aggregate mining stats */}
                  {utilityStats.aggregateMiningStats?.totalPower && (
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Combined Mining Power</span>
                      <span className={styles.statValue}>
                        {formatNumber(utilityStats.aggregateMiningStats.totalPower.current)}
                        {formatDelta(utilityStats.aggregateMiningStats.totalPower.delta)}
                      </span>
                    </div>
                  )}
                  {utilityStats.aggregateMiningStats?.avgResistance && (
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Avg Resistance</span>
                      <span className={styles.statValue}>
                        {utilityStats.aggregateMiningStats.avgResistance.current.toFixed(1)}
                        {formatDelta(utilityStats.aggregateMiningStats.avgResistance.delta, 1)}
                      </span>
                    </div>
                  )}
                  {utilityStats.aggregateMiningStats?.avgInstability && (
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Avg Instability</span>
                      <span className={styles.statValue}>
                        {utilityStats.aggregateMiningStats.avgInstability.current.toFixed(1)}
                        {formatDelta(utilityStats.aggregateMiningStats.avgInstability.delta, 1)}
                      </span>
                    </div>
                  )}
                  {utilityStats.aggregateMiningStats?.avgRange && utilityStats.aggregateMiningStats.avgRange.current > 100 && (
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Avg Range</span>
                      <span className={styles.statValue}>
                        {(utilityStats.aggregateMiningStats.avgRange.current / 1000).toFixed(2)}km
                        {utilityStats.aggregateMiningStats.avgRange.delta !== 0 && formatDelta(utilityStats.aggregateMiningStats.avgRange.delta / 1000, 2)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Salvage Equipment */}
          {utilityStats.salvageEquipment.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <Recycle size={16} />
                <span>Salvage ({utilityStats.salvageEquipment.length})</span>
                {utilityStats.salvageEquipment.some(s => s.isModified) && <span className={styles.modifiedBadge}>Modified</span>}
              </div>
              
              {/* Aggregate Summary */}
              {(() => {
                // Extract salvage head stats from hardpoints with loaded items
                let totalBeams = 0;
                let totalActiveModules = 0;
                let totalSecondaryModules = 0;
                let avgExtractionBonus = 0;
                let extractionCount = 0;

                // Recursive function to find salvage head
                const findSalvageHead = (items: any[]): any | null => {
                  if (!items) return null;
                  for (const item of items) {
                    const type = (item.itemDefinition?.type || '').toLowerCase();
                    if (type === 'salvagehead') return item;
                    if (item.childAttachments?.length > 0) {
                      const found = findSalvageHead(item.childAttachments);
                      if (found) return found;
                    }
                  }
                  return null;
                };

                utilityStats.salvageEquipment.forEach((salvage) => {
                  // Find hardpoint to get loaded item info
                  const hp = hardpoints.find(h => h.id === salvage.hardpointId);
                  if (!hp) return;

                  // Get salvage head using recursive search
                  const salvageHeadItem = hp.loadedItems ? findSalvageHead(hp.loadedItems as any[]) : null;
                  if (!salvageHeadItem) return;
                  
                  // Check for override
                  const overrideKey = `${hp.id}:${salvageHeadItem.id}`;
                  const loadedOverride = loadedItemOverrides?.get(overrideKey);
                  const headDef = loadedOverride || salvageHeadItem.itemDefinition;
                  
                  // Count beams from salvage head stats
                  const headCount = headDef?.salvageStats?.headCount || 0;
                  totalBeams += headCount;

                  // Count modules from salvage head's child attachments
                  const modules = salvageHeadItem.childAttachments || [];
                  const moduleCount = modules.length;
                  if (moduleCount > 0) totalActiveModules++;
                  if (moduleCount > 1) totalSecondaryModules++;

                  // Extract efficiency from first module (check for override)
                  if (moduleCount > 0) {
                    const firstModule = modules[0];
                    const moduleOverrideKey = `${hp.id}:${firstModule.id}`;
                    const moduleOverride = loadedItemOverrides?.get(moduleOverrideKey);
                    const moduleDef = moduleOverride || firstModule.itemDefinition;
                    
                    const desc = moduleDef?.description || '';
                    const efficiencyMatch = desc.match(/Extraction Efficiency:\s*([+-]?\d+)%/i);
                    if (efficiencyMatch) {
                      avgExtractionBonus += parseFloat(efficiencyMatch[1]);
                      extractionCount++;
                    }
                  }
                });

                return (
                  <div style={{ 
                    marginBottom: '0.75rem', 
                    padding: '0.5rem', 
                    background: 'rgba(0, 180, 255, 0.08)', 
                    borderRadius: '4px', 
                    border: '1px solid rgba(0, 180, 255, 0.2)' 
                  }}>
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Total Beams</span>
                      <span className={styles.statValue} style={{ color: '#00ff80', fontWeight: 600 }}>
                        {totalBeams}
                      </span>
                    </div>
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Active Modules</span>
                      <span className={styles.statValue} style={{ color: '#00d4ff', fontWeight: 600 }}>
                        {totalActiveModules}
                      </span>
                    </div>
                    {totalSecondaryModules > 0 && (
                      <div className={styles.statRow}>
                        <span className={styles.statLabel}>Secondary</span>
                        <span className={styles.statValue} style={{ color: '#ffaa00', fontWeight: 600 }}>
                          {totalSecondaryModules}
                        </span>
                      </div>
                    )}
                    {extractionCount > 0 && (
                      <div className={styles.statRow}>
                        <span className={styles.statLabel}>Avg Extraction</span>
                        <span className={styles.statValue} style={{ color: '#00ff80', fontWeight: 600 }}>
                          +{Math.round(avgExtractionBonus / extractionCount)}%
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Individual Salvage Arms */}
              {utilityStats.salvageEquipment.map((salvage, idx) => {
                // Get hardpoint to extract detailed salvage head info
                const hp = hardpoints.find(h => h.id === salvage.hardpointId);
                if (!hp) return null;

                const override = hardpointOverrides.get(salvage.hardpointId);
                
                // Find salvage head recursively in loadedItems (same as PowerManagementPanel)
                const findSalvageHead = (items: any[]): any | null => {
                  if (!items) return null;
                  for (const item of items) {
                    const type = (item.itemDefinition?.type || '').toLowerCase();
                    if (type === 'salvagehead') return item;
                    if (item.childAttachments?.length > 0) {
                      const found = findSalvageHead(item.childAttachments);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                
                const salvageHeadItem = hp.loadedItems ? findSalvageHead(hp.loadedItems as any[]) : null;
                if (!salvageHeadItem) return null;
                
                // Check for override on this loaded item
                const overrideKey = `${hp.id}:${salvageHeadItem.id}`;
                const loadedOverride = loadedItemOverrides?.get(overrideKey);
                const headDef = loadedOverride || salvageHeadItem.itemDefinition;
                
                const headStats = headDef?.salvageStats;
                const modules = salvageHeadItem.childAttachments || [];
                
                // Apply overrides to modules
                const activeModule = modules[0];
                const activeModuleOverrideKey = activeModule ? `${hp.id}:${activeModule.id}` : null;
                const activeModuleOverride = activeModuleOverrideKey ? loadedItemOverrides?.get(activeModuleOverrideKey) : null;
                const activeModuleDef = activeModuleOverride || activeModule?.itemDefinition;
                
                const secondaryModule = modules[1];
                const secondaryModuleOverrideKey = secondaryModule ? `${hp.id}:${secondaryModule.id}` : null;
                const secondaryModuleOverride = secondaryModuleOverrideKey ? loadedItemOverrides?.get(secondaryModuleOverrideKey) : null;
                const secondaryModuleDef = secondaryModuleOverride || secondaryModule?.itemDefinition;
                
                // Extract module stats
                const extractAllStats = (description: string) => {
                  const stats: { label: string; value: string }[] = [];
                  const statPattern = /([A-Za-z\s]+):\s*([+-]?\d+\.?\d*%?[a-zA-Z]*)/g;
                  let match;
                  while ((match = statPattern.exec(description)) !== null) {
                    let label = match[1].trim();
                    if (label.startsWith('n') && label.length > 1 && label[1] === label[1].toUpperCase()) {
                      label = label.substring(1);
                    }
                    stats.push({ label, value: match[2] });
                  }
                  return stats;
                };
                
                const activeStats = activeModuleDef?.description 
                  ? extractAllStats(activeModuleDef.description) 
                  : [];
                const secondaryStats = secondaryModuleDef?.description 
                  ? extractAllStats(secondaryModuleDef.description) 
                  : [];
                
                return (
                  <div key={salvage.hardpointId} className={styles.subItem}>
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Arm</span>
                      <span className={styles.statValue}>{salvage.name}</span>
                    </div>
                    
                    {/* Salvage Head Stats */}
                    {headStats && (
                      <>
                        {headStats.headCount > 0 && (
                          <div className={styles.statRow}>
                            <span className={styles.statLabel}>Beams</span>
                            <span className={styles.statValue}>{headStats.headCount}</span>
                          </div>
                        )}
                        {headStats.armingDistance > 0 && (
                          <div className={styles.statRow}>
                            <span className={styles.statLabel}>Arming Distance</span>
                            <span className={styles.statValue}>{headStats.armingDistance}m</span>
                          </div>
                        )}
                        {headStats.usesCargoGrid !== undefined && (
                          <div className={styles.statRow}>
                            <span className={styles.statLabel}>Cargo Grid</span>
                            <span className={styles.statValue}>{headStats.usesCargoGrid ? 'Yes' : 'No'}</span>
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Active Module */}
                    {activeModule && (
                      <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(0, 180, 255, 0.15)' }}>
                        <div className={styles.statRow}>
                          <span className={styles.statLabel} style={{ color: '#00ff80' }}>Active Module</span>
                          <span className={styles.statValue} style={{ fontSize: '0.7rem' }}>
                            {activeModuleDef?.localizedName || activeModuleDef?.itemName || 'Module'}
                          </span>
                        </div>
                        {activeStats.map((stat, i) => (
                          <div key={i} className={styles.statRow} style={{ paddingLeft: '0.5rem' }}>
                            <span className={styles.statLabel} style={{ fontSize: '0.7rem', opacity: 0.8 }}>{stat.label}</span>
                            <span className={styles.statValue} style={{ fontSize: '0.7rem', color: '#00ff80' }}>{stat.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Secondary Module */}
                    {secondaryModule && (
                      <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255, 170, 0, 0.15)' }}>
                        <div className={styles.statRow}>
                          <span className={styles.statLabel} style={{ color: '#ffaa00' }}>Secondary Module</span>
                          <span className={styles.statValue} style={{ fontSize: '0.7rem' }}>
                            {secondaryModuleDef?.localizedName || secondaryModuleDef?.itemName || 'Module'}
                          </span>
                        </div>
                        {secondaryStats.map((stat, i) => (
                          <div key={i} className={styles.statRow} style={{ paddingLeft: '0.5rem' }}>
                            <span className={styles.statLabel} style={{ fontSize: '0.7rem', opacity: 0.8 }}>{stat.label}</span>
                            <span className={styles.statValue} style={{ fontSize: '0.7rem', color: '#ffaa00', opacity: 0.8 }}>{stat.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Power</span>
                      <span className={styles.statValue}>
                        {salvage.pips.current} pips
                        {formatDelta(salvage.pips.delta, 0, true)}
                      </span>
                    </div>
                  </div>
                );
              })}
              {utilityStats.salvageEquipment.length > 1 && (
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Total Power</span>
                  <span className={styles.statValue}>
                    {utilityStats.totalPips.salvage.current} pips
                    {formatDelta(utilityStats.totalPips.salvage.delta, 0, true)}
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LoadoutStatsPanel;
